import {
  LoaderMotion,
  Pose,
  restPose,
  RING,
  RING_PHASE,
  ringPoint,
  shapeCenter,
  ShapeName,
  shapeSize,
} from "./pimo-loader-engine";

const clamp01 = (x: number) => Math.min(Math.max(x, 0), 1);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smoothstep = (x: number) => {
  const t = clamp01(x);
  return t * t * (3 - 2 * t);
};
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2;
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
const easeInCubic = (t: number) => t ** 3;
const easeOutBack = (t: number) => {
  const c1 = 1.70158;
  return 1 + (c1 + 1) * (t - 1) ** 3 + c1 * (t - 1) ** 2;
};

/** progress of a sub-animation running from `start` for `length` of the cycle */
const window01 = (p: number, start: number, length: number) =>
  clamp01((p - start) / length);

/**
 * Loading: the logo turns once as a carousel around a vertical axis and snaps back together.
 * The ring widens while turning so shapes never overlap when they pass each other.
 */
const loading: LoaderMotion = (() => {
  const SPIN = 0.75;
  const DEPTH = 0.3;
  const SPREAD = 0.42;
  const SPREAD_ANGLE = (40 / 180) * Math.PI;

  const perspective = (angle: number) =>
    1 - (DEPTH * (1 - Math.cos(angle))) / 2;

  return {
    duration: 3,
    pose(name, p) {
      const turn = 2 * Math.PI * easeInOutCubic(clamp01(p / SPIN));
      const spread =
        1 +
        SPREAD * smoothstep(Math.min(turn, 2 * Math.PI - turn) / SPREAD_ANGLE);
      const phase = RING_PHASE[name];
      const angle = phase + turn;
      const [rx, ry] = ringPoint(angle, spread);
      const [bx, by] = ringPoint(phase);
      const [cx, cy] = shapeCenter(name);

      return {
        x: cx + rx - bx,
        y: cy + ry - by,
        s: perspective(angle) / perspective(phase),
        z: Math.cos(angle),
      };
    },
  };
})();

/**
 * Generating: the shapes hop one slot further around the logo, four times, taking on each slot's
 * size, so the colours remix through the logo until everything is back in place.
 */
const generating: LoaderMotion = (() => {
  const STEPS = 4;
  const MOVE = 0.55; // fraction of a step spent moving; the rest holds the remixed logo
  const SPREAD = 0.55;
  const SHRINK = 0.35;
  const TAU = 2 * Math.PI;
  // slots in ring order (front, right, back, left)
  const slots: ShapeName[] = ["green", "yellow", "orange", "cyan"];

  return {
    duration: 3.6,
    pose(name, p) {
      const step = Math.min(Math.floor(p * STEPS), STEPS - 1);
      const t = easeInOutCubic(clamp01((p * STEPS - step) / MOVE));
      const index = slots.indexOf(name);
      const from = slots[(index + step) % STEPS];
      const to = slots[(index + step + 1) % STEPS];
      const arc = Math.sin(Math.PI * t);

      // travel along the (widened) ring; the slot offsets from the ring blend from one to the other
      const fromAngle = RING_PHASE[from];
      const turn = (((RING_PHASE[to] - fromAngle) % TAU) + TAU) % TAU;
      const angle = fromAngle + turn * t;
      const [rx, ry] = ringPoint(angle, 1 + SPREAD * arc);
      const [fx, fy] = shapeCenter(from);
      const [tx, ty] = shapeCenter(to);
      const [fbx, fby] = ringPoint(fromAngle);
      const [tbx, tby] = ringPoint(RING_PHASE[to]);
      const size = lerp(shapeSize(from), shapeSize(to), t);

      return {
        x: rx + lerp(fx - fbx, tx - tbx, t),
        y: ry + lerp(fy - fby, ty - tby, t),
        s: (size / shapeSize(name)) * (1 - SHRINK * arc),
        z: Math.cos(angle),
      };
    },
  };
})();

/**
 * Starting: the logo assembles from its centre, back to front with a little overshoot, holds,
 * then folds away again.
 */
const starting: LoaderMotion = (() => {
  const order: ShapeName[] = ["orange", "cyan", "yellow", "green"];
  const STAGGER = 0.09;
  const GROW = 0.26;
  const EXIT_START = 0.8;
  const EXIT = 0.12;

  return {
    duration: 2.8,
    pose(name, p) {
      const rest = restPose(name);
      const enter = window01(p, order.indexOf(name) * STAGGER, GROW);
      const exit = window01(p, EXIT_START, EXIT);
      const s = easeOutBack(enter) * (1 - easeInCubic(exit));
      const toward = easeOutCubic(enter) * (1 - easeInCubic(exit));

      return {
        x: lerp(RING.x, rest.x, toward),
        y: lerp(RING.y, rest.y, toward),
        s: Math.max(s, 0),
        z: rest.z,
      };
    },
  };
})();

/**
 * Thinking: a wave runs through the logo from left to right, each shape hopping in turn.
 */
const thinking: LoaderMotion = (() => {
  const order: ShapeName[] = ["cyan", "orange", "green", "yellow"];
  const STAGGER = 0.11;
  const HOP_LENGTH = 0.32;
  const HOP = 6;

  return {
    duration: 1.8,
    pose(name, p): Pose {
      const rest = restPose(name);
      const lift = Math.sin(
        Math.PI * window01(p, order.indexOf(name) * STAGGER, HOP_LENGTH)
      );
      return { ...rest, y: rest.y - HOP * lift, s: 1 + 0.06 * lift };
    },
  };
})();

export const PIMO_LOADER_MOTIONS = {
  loading,
  generating,
  starting,
  thinking,
} satisfies Record<string, LoaderMotion>;

export type PimoLoaderVariant = keyof typeof PIMO_LOADER_MOTIONS;

export const PIMO_LOADER_VARIANTS = Object.keys(
  PIMO_LOADER_MOTIONS
) as PimoLoaderVariant[];
